import { SimpleDirectedGraph } from "../../graph/directed/simple-directed-graph";
import { ColorTreeGrid } from "../colorTree/color-tree-grid";
import { MultiSetTreeNode } from "./multi-set-tree-node";
import { Edge } from "../../graph/lib/edge";
import { Node } from "../../graph/lib/node";
import { TreeEdge } from "../lib/tree-edge";
/**
 * The abstract class TreeBuilder extends the abstract
 * class SimpleDirectedGraph and is responsible for 
 * creating tree diagrams. This class uses tree diagrams
 * to visualize combinatorial problems by systematically
 * listing all possibilities in a tree.
 */
export abstract class MultiSetTreeBuilder extends SimpleDirectedGraph {
    protected set: [any[], number][]; //Tuples of alphabet + length of word from this alphabet
    protected depth: number;
    protected root: MultiSetTreeNode;

    protected selectedNode: MultiSetTreeNode;
    protected hasRepetition: boolean;
    static readonly minRow: number = 8;
    static readonly minCol: number = 13;

    constructor(set: [any[], number][], hasRepetition: boolean, canvas: HTMLCanvasElement) {
        let row = 0;
        let col = 1;
        let depth = 0;

        set.forEach(s => {
            if (hasRepetition) {
                col = col * Math.pow(s[0].length, s[1]);
            }
            else {
                for (let i = 0; i < s[1]; i++) {
                    col = col * (s[0].length - i);
                }
            }
            depth = depth + Number(s[1]);
            //console.log("Alphabet: " + s[0] + " haslength: " + s[1])
        })

       // console.log("depth: " + depth);
        row = Math.max(depth * 2 + 4, MultiSetTreeBuilder.minRow); //We need 1 row space between each level
        col = Math.max(col+4, MultiSetTreeBuilder.minCol);

        //console.log("Row: " + row);
        //console.log("Col: " + col);
        super(canvas, row, col);

        this.hasRepetition = hasRepetition;
        this.setGrid(new ColorTreeGrid(row, col, canvas));
        this.set = set;
        this.depth = depth;

        this.update();
        this.selectedNode = null;
        //TEST
    }
   

    buildTree(){
        let root: [number, number] = this.getCTGrid().getRootCoordinates();
        let rootNode = this.addTreeNode(root[0], root[1], []);
        this.nodes.push(rootNode);
        this.root = rootNode;

        let level = root[0] + 2;
        let parentsQueue: MultiSetTreeNode[] = [this.root];

        for(let i = 0; i < this.depth; i++){
            
            let distance = this.cols/ (this.getNumberChild(i)+1); //+1,
            for(let j = 0; j < this.getNumberChild(i)/this.childrenSize(i); j++){
                let parent: MultiSetTreeNode = parentsQueue.shift();
                let parentContent: any[] = parent.getContent();
                
              //  console.log("Parentcontent :" + parentContent);

                for(let k = 0; k < this.childrenSize(i); k++){
                    let node = this.addTreeNode(level,  (j*this.childrenSize(i)+k+1)*distance, 
                        this.getChildContent(parentContent, i, k), distance);
                    parent.addChildren(node);
                    parentsQueue.push(node);
                    this.nodes.push(node);
                    this.addEdge(parent,node);
                   // console.log("Child content: " + node.getContent());
                }
            }

            level = level + 2;
        }
        this.update();
    }

    addEdge(from: Node, to: Node): Edge{
        let treeEdge = new TreeEdge(from ,to)   
        treeEdge.setNearFactor(1);
        this.edges.push(treeEdge);
        return treeEdge;
    }

    //Compute the content of the child given depth and the parentcontent
    protected getChildContent(parentContent: any[], depth: number, childIndex: number){
        let childContent: any[] = [];
        for(let i = 0; i < parentContent.length; i++){
            childContent.push(parentContent[i]);
        }
        let newElem: any = this.getElementAtDepthAndOffset(parentContent, depth, childIndex);
        childContent.push(newElem);
        return childContent;
    }

    private getElementAtDepthAndOffset(parentContent: any[], depth: number, childIndex : number): any{        
        let alpIndex = 0;
        let sumD = 0;
        let offsetAlphabet = 0;

       
        //First we compute which alphabet is beeing used at depth d
        for(let i = 0; i < this.set.length; i++){
            let offset:number = this.set[i][1];
            let upperbound:number = sumD + offset;
            if(sumD <= depth && depth < upperbound){
                alpIndex = i; 
                offsetAlphabet = depth - sumD;
                
                break;
            }
            sumD = sumD + offset;
        }

        if(this.hasRepetition){
            return this.set[alpIndex][0][childIndex];
        }

        else{
            
            let contentOfSet: any[] = [];
            for(let i = 0; i < offsetAlphabet; i++){
                //Simulate peek operation + storing the elements which already are contained in the parent from this set
              
                contentOfSet.push(parentContent[parentContent.length-1-i]);
            }
            let wholeSet: any[] = this.set[alpIndex][0];
          
            let count = 0;
            let index = 0;

            //console.log("WHoleSET: " + wholeSet);
            //console.log("Parentset: " + contentOfSet);
            for(let i = 0; i < wholeSet.length; i++){
                if(!contentOfSet.includes(wholeSet[i])){
                    count = count + 1;
                }

                if(count == childIndex+1){
                    index = i;
                    break;
                } 
            }
            return wholeSet[index];

        }



    }

    protected abstract addTreeNode(row: number, col: number, content: any[], maxWidthTextCol?: number): MultiSetTreeNode;
        /* 
        * @param curDepth = 0 is root 
        * @returns the number of children at depth @param curDepth
        */
    protected getNumberChild(curDepth: number): number {
        let res = this.childrenSize(0);
        for(let i = 1; i <= curDepth; i++){
            res = res*this.childrenSize(i);
        }
        return res;
    }

    /**
        * 
        * @param d = 0, root node
        * @returns the number of children of a single node at level d (starting from offset 0)
        * 
        * This method is only used by buildTree
        */
    protected childrenSize(d: number): number {
        let alpIndex = 0;
        let sumD = 0;
        let offsetAlphabet = 0;

        //First we compute which alphabet is beeing used at depth d
        for(let i = 0; i < this.set.length; i++){
            let offset = this.set[i][1];
            if(sumD <= d && d < sumD+offset){
                alpIndex = i; 
                offsetAlphabet = d - sumD;
                break;
            }
            sumD = sumD + offset;
        }

        if(this.hasRepetition){
            return this.set[alpIndex][0].length;
        }

        if(this.set[alpIndex][0].length - offsetAlphabet <= 0){
            return 1;
        }
        return this.set[alpIndex][0].length - offsetAlphabet;
    }

    getSet(): [any[], number][] {
        return this.set;
    }

    getDepth(): number {
        return this.depth;
    }

    setHasRepetition(rep: boolean) {
        this.hasRepetition = rep;
    }

    getHasRepetition() {
        return this.hasRepetition;
    }

    getCTGrid(): ColorTreeGrid {
        if (this.grid instanceof ColorTreeGrid) {
            return this.grid;
        }
    }

    setLevelVisible(visible: boolean) {
        this.getCTGrid().setLevelsVisible(visible);
    }
}


